import { Link } from 'react-router-dom';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import ProductsDemo from '../components/marketing/ProductsDemo';
import {
  CheckCircle2,
  FileText,
  Shield,
  ArrowRight,
  Briefcase,
  UserCheck,
  Database,
  Upload,
  Sparkles,
  AlertTriangle,
  Clock,
  Mail,
  TrendingDown,
  Smartphone,
  PenTool,
  Zap,
  Award,
  Bell,
  ListChecks,
  MousePointerClick,
  FileSignature,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';

const DEMO_MAILTO = 'mailto:hello@hyredocs.com?subject=HyreDocs%20Demo%20Request';

/* ======================== REVEAL WRAPPER ======================== */
function Reveal({ children, className = '', delay = 0, y = 40 }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? {} : { opacity: 0, y }}
      whileInView={reduced ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ======================== NAV ======================== */
function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { href: '#problem', label: 'The Problem' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#products', label: 'Products' },
    { href: '#impact', label: 'Impact' },
    { href: '#bullhorn', label: 'Bullhorn' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <nav className="w-full border-b border-slate-200/60 bg-white/70 backdrop-blur-xl sticky top-0 z-50" data-testid="home-nav">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="home-nav-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-sm">
            <HyreDocsIcon className="w-5 h-5" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">HyreDocs</span>
        </Link>

        <div className="hidden lg:flex items-center gap-7 text-sm text-slate-500">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="relative hover:text-slate-900 transition-colors duration-200 after:absolute after:-bottom-0.5 after:left-0 after:w-0 after:h-[2px] after:bg-[#f97316] after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="home-nav-signin" className="hidden sm:inline-flex">
            <Button variant="ghost" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all duration-200">
              Sign in
            </Button>
          </Link>
          <a href={DEMO_MAILTO} data-testid="home-nav-demo" className="hidden sm:inline-flex">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white transition-all duration-200 hover:shadow-lg hover:shadow-slate-900/20">
              Book a demo
            </Button>
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="home-nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-slate-100 bg-white overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-2.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <div className="pt-3 flex flex-col gap-2 border-t border-slate-100 mt-2">
                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" className="w-full">Sign in</Button>
                </Link>
                <a href={DEMO_MAILTO} onClick={() => setMobileOpen(false)}>
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">Book a demo</Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

/* ======================== HERO ======================== */
function Hero() {
  const trustPoints = [
    'ESIGN, UETA & eIDAS compliant',
    'Mobile-first candidate experience',
    'Tamper-evident audit trail',
  ];

  const stats = [
    { value: '10 min', label: 'Average completion' },
    { value: '< 1 day', label: 'Typical onboarding cycle' },
    { value: '100%', label: 'Audit-ready documents' },
    { value: '0', label: 'Paper forms required' },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#FFFBF5] via-[#FAF9F7] to-white">
      {/* Decorative blurs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#f97316]/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-[500px] h-[500px] bg-gradient-to-br from-[#f59e0b]/6 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-20">
        <div className="max-w-4xl">
          <Reveal y={20}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-7">
              <Sparkles className="w-3.5 h-3.5 text-[#f97316]" />
              <span className="text-xs font-medium text-slate-700">
                Documents <span className="text-slate-300 mx-0.5">·</span> Online Forms <span className="text-slate-300 mx-0.5">·</span> ATS-native
              </span>
            </div>
          </Reveal>

          <Reveal y={24} delay={0.05}>
            <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 leading-[1.02] tracking-tight">
              Stop chasing paperwork.<br />
              <span className="bg-gradient-to-r from-[#f97316] to-[#e8590c] bg-clip-text text-transparent">Start placing candidates.</span>
            </h1>
          </Reveal>

          <Reveal y={20} delay={0.1}>
            <p className="mt-7 text-lg lg:text-xl text-slate-600 max-w-2xl leading-relaxed">
              HyreDocs turns manual onboarding packs, client contracts, <span className="text-slate-900 font-semibold">and online forms</span> into a 10-minute candidate experience. Send, sign, collect — from any device, fully audited, automatically reconciled back to your ATS.
            </p>
          </Reveal>

          <Reveal y={16} delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href={DEMO_MAILTO} data-testid="hero-demo">
                <Button className="h-12 px-7 bg-slate-900 hover:bg-slate-800 text-white gap-2 text-base font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-slate-900/20 hover:-translate-y-0.5">
                  Book a 15-min demo
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <Link to="/login" data-testid="hero-signin">
                <Button variant="outline" className="h-12 px-7 border-slate-300 text-base hover:bg-slate-50 transition-all duration-200">
                  Sign in
                </Button>
              </Link>
            </div>
          </Reveal>

          <Reveal y={12} delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
              {trustPoints.map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* Stats strip — merged from TrustStats */}
      <div className="border-t border-slate-100" data-testid="trust-stats">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} y={20}>
                <div className="text-center md:text-left">
                  <div className="font-heading text-3xl lg:text-4xl font-bold text-slate-900">{s.value}</div>
                  <div className="text-sm text-slate-500 mt-1">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ======================== THE PROBLEM ======================== */
function Problem() {
  const pains = [
    {
      icon: Mail,
      title: 'Documents arrive as email attachments',
      body: 'Candidates print, scan, re-email. Half never return. Recruiters spend hours chasing.',
    },
    {
      icon: AlertTriangle,
      title: 'Compliance gaps creep in',
      body: "Right-to-work, DBS, IR35 paperwork sits in inboxes and shared drives. You can't prove what was signed or when.",
    },
    {
      icon: Clock,
      title: 'Placements delay by days',
      body: 'Contract signed Tuesday, returned Friday, filed Monday. Candidates start a week late — or not at all.',
    },
    {
      icon: TrendingDown,
      title: 'Data lives in 3 places at once',
      body: 'Candidate details retyped into Word templates, emailed PDFs, and your ATS. Nothing stays in sync.',
    },
  ];

  return (
    <section id="problem" className="py-20 lg:py-28 bg-slate-50" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">The problem</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              Recruitment paperwork is still stuck in 2005.
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Every placement generates a packet of documents — and every step of that packet is a friction point. Multiply by volume and the impact is measurable: lost placements, compliance risk, and recruiter time that should be selling.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pains.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300 h-full group">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <p.icon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2 leading-snug">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== HOW IT WORKS ======================== */
function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Build a template — or a form',
      body: 'Upload a PDF and drop fields on visually, or build an online form with the drag-and-drop builder.',
    },
    {
      n: '02',
      title: 'Send in one click',
      body: 'From any candidate, contact, or placement record. Details pre-fill from Bullhorn. No retyping.',
    },
    {
      n: '03',
      title: 'Recipient completes on mobile',
      body: 'Candidates sign PDFs or complete forms on their phone in minutes. Type-to-sign, upload IDs, answer questions — all in-browser.',
    },
    {
      n: '04',
      title: 'Everything files itself',
      body: 'Completed PDFs and form submissions land in Bullhorn — Files tab + field write-back — with audit certificate.',
    },
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-white" data-testid="solution-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl mb-16">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">How it works</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              One flow. From template to filed.
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              HyreDocs replaces the email-PDF-chasing cycle with a single, audited workflow. Built for the shape of agency recruitment: many templates, many senders, many recipients, one source of truth.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, idx) => (
            <Reveal key={s.n} delay={idx * 0.1}>
              <div className="relative h-full">
                <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-6 border border-slate-200 h-full hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center font-mono text-xs font-bold group-hover:bg-[#f97316] transition-colors duration-300">
                      {s.n}
                    </div>
                    {idx < steps.length - 1 && (
                      <div className="hidden lg:block flex-1 h-px bg-gradient-to-r from-slate-300 to-transparent" />
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 mb-2 text-lg leading-snug">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== TWO PRODUCTS ======================== */
function TwoProducts() {
  const products = [
    {
      tag: 'Documents',
      title: 'PDFs, signed and filed',
      icon: FileSignature,
      tagline: 'When the document has to look exactly like the source PDF.',
      gradient: 'from-orange-50 via-amber-50 to-white',
      ring: 'ring-amber-200',
      accent: 'text-orange-700',
      iconBg: 'from-[#f97316] to-[#f59e0b]',
      bullets: [
        'Upload any PDF — drop fields visually or let AI suggest them',
        'Type-to-sign or draw signatures, legally binding',
        'Tamper-evident audit certificate on every completion',
        'Right-to-work, DBS, contracts, P45s, placement agreements',
      ],
      footer: 'Use it for: onboarding packs, client contracts, signed forms.',
    },
    {
      tag: 'Online Forms',
      title: 'Drag-and-drop online forms',
      icon: ListChecks,
      tagline: "When you just need the answers — no PDF required.",
      gradient: 'from-violet-50 via-indigo-50 to-white',
      ring: 'ring-violet-200',
      accent: 'text-violet-700',
      iconBg: 'from-violet-500 to-indigo-500',
      bullets: [
        'Drag-and-drop builder with 20+ field types (name, signature, ratings, file upload)',
        'Multi-page forms with conditional logic and progress bars',
        'AI form generator — describe it, get a working form in seconds',
        'Per-field Bullhorn pre-fill and write-back toggles',
      ],
      footer: 'Use it for: candidate intake, references, surveys, compliance checks.',
    },
  ];

  return (
    <section id="products" className="py-20 lg:py-28 bg-white" data-testid="two-products-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">Two products, one platform</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              Documents <span className="text-slate-400 font-normal">+</span> Online Forms.<br />
              <span className="bg-gradient-to-r from-[#f97316] to-[#e8590c] bg-clip-text text-transparent">Pick the right tool for the job.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Sometimes you need a signed PDF that looks exactly like the source contract. Sometimes you just need the candidate&apos;s data captured cleanly. HyreDocs covers both — and both write back to the same Bullhorn record.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mb-14">
            <ProductsDemo />
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.tag} delay={i * 0.12}>
              <div
                className={`rounded-2xl p-8 bg-gradient-to-br ${p.gradient} ring-1 ${p.ring} flex flex-col h-full hover:shadow-lg transition-all duration-300`}
                data-testid={`product-card-${p.tag.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className={`text-xs font-semibold uppercase tracking-wider ${p.accent}`}>{p.tag}</div>
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.iconBg} shadow-sm flex items-center justify-center`}>
                    <p.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-slate-900 text-2xl mb-2 leading-tight">{p.title}</h3>
                <p className="text-slate-600 italic mb-6">{p.tagline}</p>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-slate-700 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-5 border-t border-slate-200/70">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{p.footer}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 rounded-2xl bg-slate-50 border border-slate-200 p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-5">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center flex-shrink-0">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-heading font-bold text-slate-900 text-lg mb-1">Same Bullhorn record, either way.</h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Documents and Forms share the same field-mapping engine — choose to pre-fill from Bullhorn, write back on submit, or both. Completed forms land in the Files tab as PDFs; signed documents land in the Files tab as PDFs. The recruiter view stays consistent.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ======================== IMPACT (merged Results + WhyHyreDocs) ======================== */
function Impact() {
  const metrics = [
    { n: '90%', label: 'fewer emails chasing signatures', sub: 'Automated reminders and mobile-first signing handle the nudging for you.' },
    { n: '5 days', label: 'cut from onboarding cycle', sub: 'From contract-sent to candidate-started, paperwork stops being the bottleneck.' },
    { n: '100%', label: 'audit-ready documents', sub: 'Every completion generates a tamper-evident certificate. No spreadsheets needed.' },
  ];

  const outcomes = [
    {
      icon: Zap,
      title: 'Activate candidates faster',
      body: 'Cut the gap between offer and start date. Mobile-first signing means paperwork completes before the kettle boils.',
    },
    {
      icon: Shield,
      title: 'Bulletproof compliance',
      body: 'Every signed document carries a tamper-evident audit certificate: IP, timestamp, SHA-256 hash. Always traceable.',
    },
    {
      icon: UserCheck,
      title: 'Close contracts, not inboxes',
      body: 'Client contracts and candidate onboarding from the same platform. Terms of business, placement agreements — all signed-and-synced.',
    },
    {
      icon: Database,
      title: 'Data that stays in sync',
      body: 'No more retyping. Candidate details flow from your ATS into templates; signed data and files flow back automatically.',
    },
  ];

  return (
    <section id="impact" className="py-20 lg:py-28 bg-slate-50" data-testid="impact-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">Impact</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              What changes the day you start using it.
            </h2>
          </div>
        </Reveal>

        {/* Metric cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-16">
          {metrics.map((r, i) => (
            <Reveal key={r.label} delay={i * 0.1}>
              <div className="p-8 rounded-2xl bg-gradient-to-br from-[#FFF8F0] to-[#FFEFDB] border border-orange-100 hover:shadow-lg transition-all duration-300 h-full">
                <div className="font-heading text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#f97316] to-[#e8590c] bg-clip-text text-transparent leading-none">{r.n}</div>
                <div className="font-semibold text-slate-900 mt-3">{r.label}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Outcome cards */}
        <div className="grid md:grid-cols-2 gap-5">
          {outcomes.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.08}>
              <div className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-md transition-all duration-300 h-full group">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center mb-5 shadow-sm group-hover:scale-110 transition-transform duration-300">
                  <o.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 text-xl mb-3">{o.title}</h3>
                <p className="text-slate-600 leading-relaxed">{o.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== USE CASES ======================== */
function UseCases() {
  const cases = [
    {
      icon: UserCheck,
      tag: 'Candidates',
      title: 'Onboarding packs that actually complete',
      gradient: 'from-blue-50 to-indigo-50',
      ring: 'ring-blue-200',
      accent: 'text-blue-700',
      items: [
        'Application forms & starter packs',
        'Right-to-work and DBS verification',
        'P45s, P46s, new-starter checklists',
        'ID uploads that file themselves',
      ],
    },
    {
      icon: Briefcase,
      tag: 'Clients',
      title: 'Contract workflows without the back-and-forth',
      gradient: 'from-amber-50 to-orange-50',
      ring: 'ring-amber-200',
      accent: 'text-orange-700',
      items: [
        'Terms of business',
        'Placement agreements',
        'Variation letters and renewals',
        'Statements of work',
      ],
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="use-cases-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-2xl mb-14">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">Built for both sides</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              Every document a recruitment agency sends. One platform.
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Most signing tools assume you only send to candidates. HyreDocs handles both sides of the deal — same dashboard, same audit trail, same data flowing in and out of your ATS.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {cases.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div className={`rounded-2xl p-8 bg-gradient-to-br ${c.gradient} ring-1 ${c.ring} h-full hover:shadow-lg transition-all duration-300`}>
                <div className="flex items-start justify-between mb-5">
                  <div className={`text-xs font-semibold uppercase tracking-wider ${c.accent}`}>{c.tag}</div>
                  <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                    <c.icon className={`w-5 h-5 ${c.accent}`} />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-slate-900 text-xl mb-5 leading-snug">{c.title}</h3>
                <ul className="space-y-2.5">
                  {c.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-slate-500 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== BULLHORN ======================== */
function BullhornSection() {
  const points = [
    { icon: Database, title: 'Deep field mapping', body: 'Map any template field to any Bullhorn field — including custom ones. Pull in, write back, or both.' },
    { icon: Upload, title: 'Native file attachments', body: 'Uploaded IDs, DBS certs, and documents attach to the Bullhorn record automatically on completion.' },
    { icon: Zap, title: 'Embedded inside Bullhorn', body: 'Recruiters see pending and signed documents directly on every candidate record. No tab-switching.' },
    { icon: Shield, title: 'Credential-safe auth', body: 'Bullhorn passwords are validated against Bullhorn. HyreDocs never stores or sees them.' },
    { icon: Award, title: 'Credentials tab write-back', body: 'Map a template to a Bullhorn credential — completion creates the Credential, sets the expiry, and attaches the signed PDF.' },
    { icon: Bell, title: 'Expiry reminders, automated', body: "Daily scanner emails candidates 30 + 7 days before any credential expires. Recruiter is CC'd. Zero spreadsheets." },
  ];

  return (
    <section id="bullhorn" className="py-20 lg:py-28 bg-slate-900 text-white relative overflow-hidden" data-testid="bullhorn-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#f97316]/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#f59e0b]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">Bullhorn-native</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold leading-[1.1]">
              Built for Bullhorn agencies, not retrofitted for them.
            </h2>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed">
              Most e-signature tools bolt onto Bullhorn via Zapier and call it done. HyreDocs was designed around Bullhorn&apos;s data model: send from any entity, pre-fill from any field, write back to any field, attach files to the right record.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-5">
          {points.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 h-full group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#f97316]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#f97316]/30 transition-colors duration-300">
                    <p.icon className="w-5 h-5 text-[#f97316]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold mb-1.5 text-lg">{p.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== PLATFORM CAPABILITIES (compact grid) ======================== */
function Capabilities() {
  const features = [
    { icon: FileText, title: 'PDF template editor', body: 'Drop fields on any PDF. Save once, send forever.' },
    { icon: ListChecks, title: 'Form builder', body: 'Drag-and-drop online forms. No PDF needed.' },
    { icon: MousePointerClick, title: 'AI form generator', body: 'Describe the form. Get a working draft in seconds.' },
    { icon: Smartphone, title: 'Mobile-first', body: 'Candidates complete on any device. No app required.' },
    { icon: PenTool, title: 'Type-to-sign or draw', body: 'Two signature modes. Both legally binding.' },
    { icon: UserCheck, title: 'Approval workflow', body: 'Optional reviewer step for contract-level docs.' },
    { icon: Upload, title: 'Inline file uploads', body: 'Candidates upload IDs, DBS, right-to-work inline.' },
    { icon: Award, title: 'Audit certificates', body: 'Every signed doc has a tamper-evident certificate.' },
  ];

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="capabilities-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-2xl mb-12">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">Platform</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              Everything you need. Nothing you don&apos;t.
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.05} y={24}>
              <div className="p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white hover:shadow-md transition-all duration-300 h-full group">
                <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center mb-3 group-hover:bg-[#f97316] transition-colors duration-300">
                  <f.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== FAQ ======================== */
function FAQ() {
  const faqs = [
    {
      q: 'How long does it take to get set up?',
      a: 'A typical agency is sending documents within a day. We provision your workspace, migrate your existing templates, and connect your ATS during a single onboarding session.',
    },
    {
      q: "What's the difference between Documents and Online Forms?",
      a: "Documents are signed PDFs — use them when the output has to look exactly like the source contract or compliance form (right-to-work, terms of business, P45s). Online Forms are pure data capture — drag-and-drop builder, no PDF required, designed for intake, references, surveys, and quick compliance checks. Both write back to the same Bullhorn record and both land a PDF in the Files tab on completion, so the recruiter view stays consistent regardless of which tool you use.",
    },
    {
      q: 'Can clients (not just candidates) sign documents?',
      a: 'Yes. Send to any recipient — candidate, client contact, or placement. Terms of business, placement agreements, and variation letters are first-class use cases, not afterthoughts.',
    },
    {
      q: 'Can HyreDocs update the Bullhorn Credentials tab?',
      a: "Yes. Map any HyreDocs template to a credential from your Bullhorn catalog (CSCS, GAS Safe, DBS, anything you've got configured). When the candidate completes the document, HyreDocs creates a new Candidate Credential or Placement Requirement on Bullhorn, sets the expiry from a date field the recruiter tags on the template, and attaches the signed PDF to the credential record itself — and to the Files tab. From that point a daily background job emails the candidate ahead of expiry (default 30 + 7 days out, configurable per tenant), CC'ing the recruiter so the renewal never falls off anyone's radar.",
    },
    {
      q: 'Is it legally binding?',
      a: 'Yes. HyreDocs produces e-signatures compliant with the ESIGN Act (US), UETA, and eIDAS (EU/UK). Every signed document includes a tamper-evident audit certificate with IP address, timestamp, user agent, and SHA-256 verification hash.',
    },
    {
      q: 'What happens to the signed document?',
      a: "It's stored securely in HyreDocs with the audit certificate appended, emailed to the signer and sender, and — when configured — the field values and uploaded files sync back to the corresponding ATS record automatically.",
    },
    {
      q: 'Do we need to install anything in our ATS?',
      a: "Just one thing, once: a custom tab pointing at HyreDocs. From then on, HyreDocs loads inside your ATS with a single sign-on — no per-user setup, no extensions.",
    },
    {
      q: 'Is our data safe?',
      a: 'Each agency gets a fully ring-fenced workspace — documents, templates, and credentials are never shared across tenants. ATS passwords are never stored; we validate them at the ATS on each login.',
    },
  ];

  return (
    <section id="faq" className="py-20 lg:py-28 bg-slate-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal>
          <div className="mb-12">
            <p className="text-sm font-semibold text-[#f97316] uppercase tracking-wider mb-3">FAQ</p>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              Questions we hear a lot.
            </h2>
          </div>
        </Reveal>

        <div className="space-y-3">
          {faqs.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.04} y={16}>
              <details className="group rounded-xl border border-slate-200 bg-white overflow-hidden">
                <summary className="flex items-center justify-between cursor-pointer p-5 font-semibold text-slate-900 list-none hover:bg-slate-50 transition-colors duration-200">
                  <span className="pr-4">{f.q}</span>
                  <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-transform duration-300 flex-shrink-0">+</span>
                </summary>
                <div className="px-5 pb-5 text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ======================== FINAL CTA ======================== */
function FinalCTA() {
  return (
    <section className="py-24 lg:py-32 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-[#f97316]/15 to-transparent blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.1] mb-6">
            Ready to stop chasing paperwork?
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="text-lg lg:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            15-minute demo. We&apos;ll show you your first document flowing end-to-end. If it&apos;s not a fit, you&apos;ll know in 10.
          </p>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={DEMO_MAILTO} data-testid="final-cta-demo">
              <Button className="h-12 px-8 bg-[#f97316] hover:bg-[#ea580c] text-white font-semibold gap-2 transition-all duration-200 hover:shadow-xl hover:shadow-[#f97316]/25 hover:-translate-y-0.5">
                Book a demo
                <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
            <Link to="/login" data-testid="final-cta-signin">
              <Button variant="outline" className="h-12 px-8 bg-transparent border-slate-700 text-white hover:bg-white/5 hover:text-white transition-all duration-200">
                Sign in
              </Button>
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No credit card
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Set up in a day
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> ATS-integrated from day one
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ======================== FOOTER ======================== */
function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-400 py-14">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center">
              <HyreDocsIcon className="w-5 h-5" />
            </div>
            <span className="font-heading font-bold text-white text-lg">HyreDocs</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href={DEMO_MAILTO} className="hover:text-white transition-colors duration-200">Book a demo</a>
            <a href="mailto:support@hyredocs.com" className="hover:text-white transition-colors duration-200">Support</a>
            <Link to="/login" className="hover:text-white transition-colors duration-200">Sign in</Link>
            <Link to="/privacy" className="hover:text-white transition-colors duration-200">Privacy</Link>
            <Link to="/cookies" className="hover:text-white transition-colors duration-200">Cookies</Link>
            <Link to="/legal/dpa" className="hover:text-white transition-colors duration-200">DPA</Link>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs">All systems operational</span>
            </span>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-slate-800 flex flex-wrap justify-between gap-4 text-xs text-slate-500">
          <span>&copy; {new Date().getFullYear()} HyreDocs. Built for recruitment agencies.</span>
          <span>ESIGN &middot; UETA &middot; eIDAS compliant</span>
        </div>
      </div>
    </footer>
  );
}

/* ======================== PAGE ======================== */
export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <Hero />
      <Problem />
      <HowItWorks />
      <TwoProducts />
      <Impact />
      <UseCases />
      <BullhornSection />
      <Capabilities />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
